/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { announce, clearAnnouncer } from '@react-aria/live-announcer';

import { useCoachChatContext } from 'bundles/ai-coach-platform/components/patterns/chat/context/CoachChatContext';
import SearchBar from 'bundles/ai-coach-platform/components/patterns/chat/search/SearchBar';

import _t from 'i18n!nls/ai-coach-platform';

const styles = {
  search: css`
    padding: var(--cds-spacing-100) var(--cds-spacing-200);
    height: 54px;
  `,
};

type Props = {
  onClose: () => void;
};

const FindInChatSearch = ({ onClose }: Props): React.ReactElement => {
  const { searchTerm, setSearchTerm, exitSearchMode, clearSearch, activeResultIndex, setActiveResultIndex, results } =
    useCoachChatContext();

  const handleScrollActiveResultIntoView = React.useCallback<(index: number) => void>(
    (index: number) => {
      // scrolls active result into the center of the viewport
      results[index]?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
    [results]
  );

  const announceResult = React.useCallback<(index: number) => void>(
    (index: number) => {
      clearAnnouncer('polite');
      announce(`${searchTerm}, result ${index + 1} of ${results.length}`, 'polite');
    },
    [results, searchTerm]
  );

  const updateActiveResult = React.useCallback<(newIndex: number, oldIndex: number) => void>(
    (newIndex: number, oldIndex: number) => {
      results[oldIndex]?.current?.classList.remove('active');
      results[newIndex]?.current?.classList.add('active');
      handleScrollActiveResultIntoView(newIndex);

      announceResult(newIndex);
    },
    [results, handleScrollActiveResultIntoView, announceResult]
  );

  const handleSearch = React.useCallback<(term: string) => void>(
    (term: string) => {
      setSearchTerm(term);
      if (results.length > 0) {
        setActiveResultIndex(0);
        updateActiveResult(0, 0);
      }
    },
    [results, setActiveResultIndex, setSearchTerm, updateActiveResult]
  );

  const handleClear = () => {
    clearSearch();
  };

  const handleClose = () => {
    handleClear();
    exitSearchMode();
    onClose();
  };

  const handleNavigateUp = () => {
    if (activeResultIndex === null) {
      return;
    }

    const newIndex = activeResultIndex - 1 < 0 ? results.length - 1 : activeResultIndex - 1;
    setActiveResultIndex(newIndex);

    updateActiveResult(newIndex, activeResultIndex);
  };
  const handleNavigateDown = () => {
    if (activeResultIndex === null) {
      return;
    }

    const newIndex = activeResultIndex + 1 >= results.length ? 0 : activeResultIndex + 1;

    setActiveResultIndex(newIndex);

    updateActiveResult(newIndex, activeResultIndex);
  };

  return (
    <div className="coach-find-in-chat-search" css={styles.search}>
      <SearchBar
        onSubmit={handleSearch}
        onClose={handleClose}
        onClear={handleClear}
        placeholder={_t('Find in chat')}
        aria-label={_t('Find in chat')}
        onNavigateUp={handleNavigateUp}
        onNavigateDown={handleNavigateDown}
        results={{
          current: typeof activeResultIndex === 'number' ? activeResultIndex : 0,
          total: results.length,
        }}
        skipLinkTargetRef={results[activeResultIndex ?? 0] ?? undefined}
      />
    </div>
  );
};

export default FindInChatSearch;
